<template>
  <div
      ref="shell"
      class="card-shell"
      :class="{ dragging }"
      draggable="true"
      @dragstart="handleDragStart"
      @dragenter.prevent="$emit('drag-enter', $event)"
      @dragover.prevent="$emit('drag-over', $event)"
      @drop.prevent="$emit('drop', $event)"
      @dragend="$emit('drag-end', $event)"
  >
    <button
        type="button"
        class="card-delete"
        title="Supprimer cet item du portfolio"
        aria-label="Supprimer cet item du portfolio"
        @click.stop="$emit('delete')"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path d="M11 1.5v1h3a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 10.615 16h-5.23a2 2 0 0 1-1.994-1.84L2.538 3.5H2a.5.5 0 0 1 0-1h3v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5"/>
      </svg>
    </button>

    <button
        class="card-link"
        type="button"
        @click="$emit('open')"
    >
      <div class="card" :class="{ 'no-media': !hasMedia, 'has-tech': technologyNames.length }">
        <div v-if="hasMedia" class="card-preview">
          <div class="preview-media-wrapper">
            <template v-if="mediaList[0].type === 'image'">
              <img
                  class="preview-media"
                  :src="mediaList[0].src"
                  :alt="`${item.title || 'aperçu'} - première image`"
              >
            </template>
            <template v-else-if="mediaList[0].type === 'video'">
              <video
                  class="preview-media"
                  :src="mediaList[0].src"
                  autoplay
                  muted
                  loop
                  playsinline
                  preload="metadata"
              ></video>
            </template>
            <template v-else-if="mediaList[0].type === 'pdf'">
              <iframe
                  class="preview-media"
                  :src="mediaList[0].src"
                  title="Aperçu PDF"
              ></iframe>
            </template>
            <template v-else>
              <div class="preview-file">Fichier</div>
            </template>
          </div>
        </div>

        <h2 class="card-title">{{ item.title || 'Sans titre' }}</h2>
        <p v-if="item.subTitle" class="card-subtitle">{{ item.subTitle }}</p>

        <div v-if="technologyNames.length" class="card-tech" aria-hidden="true">
          <div class="card-tech-list">
            <span
                v-for="technology in technologyNames.slice(0, 2)"
                :key="technology"
                class="tech-chip tech-chip--card"
            >
              {{ technology }}
            </span>
            <span
                v-if="technologyNames.length > 2"
                class="tech-chip tech-chip--count"
            >
              +{{ technologyNames.length - 2 }}
            </span>
          </div>
        </div>
      </div>
    </button>
  </div>
</template>

<script>
import {toPortfolioMediaList} from "@/utils/portfolioMedia";

export default {
  name: 'AdminPortfolioCard',

  props: {
    dragging: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      required: true
    }
  },

  emits: ['delete', 'drag-end', 'drag-enter', 'drag-over', 'drag-start', 'drop', 'open'],

  computed: {
    mediaList() {
      return toPortfolioMediaList(this.item?.media || []);
    },

    hasMedia() {
      return this.mediaList.length > 0;
    },

    technologyNames() {
      return (this.item?.technologies || []).map((technology) => technology.name);
    }
  },

  methods: {
    handleDragStart(event) {
      if (event?.dataTransfer && this.$refs.shell) {
        const rect = this.$refs.shell.getBoundingClientRect();
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setDragImage(this.$refs.shell, rect.width / 2, rect.height / 2);
      }

      this.$emit('drag-start', event);
    }
  }
}
</script>

<style scoped>
.card-shell {
  position: relative;
  height: 100%;
  transition: transform 0.24s cubic-bezier(0.2, 0.9, 0.2, 1), opacity 0.2s ease;
}

.card-shell.dragging {
  opacity: 0.28;
  transform: scale(0.98);
}

.card-link {
  text-decoration: none;
  color: inherit;
  display: block;
  width: 100%;
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  height: 100%;
}

.card-delete {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 4;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  color: rgb(255, 0, 0);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.18s ease, filter 0.18s ease, opacity 0.18s ease;
}

.card-delete svg {
  width: 24px;
  height: 24px;
  fill: currentColor;
}

.card-delete:hover {
  transform: scale(1.2);
  filter: drop-shadow(0 0 8px rgba(255, 0, 0, 0.4));
}

.card {
  position: relative;
  background-color: #323336;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.24s cubic-bezier(0.2, 0.9, 0.2, 1), box-shadow 0.24s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.75rem;
  box-sizing: border-box;
  height: 320px;
  overflow: hidden;
  color: white;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 0 20px #00f0ff, 0 0 30px #00f0ff66;
}

.card.no-media {
  justify-content: center;
}

.card-preview {
  margin: -0.5rem -0.5rem 0;
  border-radius: 14px;
  overflow: hidden;
}

.preview-media-wrapper {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  background: #000;
  height: 150px;
}

.preview-media,
.preview-file {
  width: 100%;
  height: 100%;
  display: block;
  background: #000;
  border: none;
}

.preview-media {
  object-fit: contain;
}

.preview-file {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d7deea;
  font-size: 0.95rem;
}

.card-title {
  color: #00f0ff;
  margin: 0;
  font-size: 1.4rem;
}

.card-subtitle {
  margin: 0;
}

.card.has-tech {
  padding-bottom: 3.5rem;
}

.card-tech {
  position: absolute;
  right: 12px;
  bottom: 12px;
}

.card-tech-list {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.tech-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.75rem;
  border: 2px solid #00f0ff;
  color: #e6faff;
  border-radius: 999px;
  background: rgba(0, 240, 255, 0.06);
  box-shadow: 0 0 8px #00f0ff33 inset;
  font-size: 0.9rem;
  white-space: nowrap;
}

.tech-chip--card {
  font-size: 0.85rem;
  padding: 0.3rem 0.65rem;
}

.tech-chip--count {
  width: 28px;
  height: 28px;
  min-width: 28px;
  padding: 0;
  font-weight: 700;
  font-size: 0.8rem;
}

@media (max-width: 650px) {
  .card {
    height: 280px;
  }
}

@media (max-width: 360px) {
  .tech-chip--card {
    font-size: 0.8rem;
    padding: 0.25rem 0.55rem;
  }

  .tech-chip--count {
    width: 24px;
    height: 24px;
    min-width: 24px;
    font-size: 0.72rem;
  }
}
</style>
